import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
// 1. Importa i componenti Chakra necessari
import { 
    Box, 
    Flex, 
    Text, 
    Heading, 
    Button, 
    Progress, 
    SimpleGrid, 
    useColorModeValue, 
    Spinner,
    Link as ChakraLink 
} from '@chakra-ui/react';

// 2. Importa le Icone da Lucide React
import { 
    Zap, 
    LogOut, 
    CloudUpload, 
    User, 
    CreditCard, 
    CheckCircle, 
    ZapOff,
    Upload,
    Download
} from 'lucide-react'; 

// -----------------------------------------------------
// 2. Dashboard Componente Core (XML_PDF_Converter_App.jsx)
// -----------------------------------------------------

const Dashboard = ({ user, setUser, handleLogout }) => {
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [conversionStatus, setConversionStatus] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState(null);
    const [downloadFile, setDownloadFile] = useState(null);

    const navigate = useNavigate();

    // Sfondo e Colori di Chakra
    const bgApp = useColorModeValue('gray.50', 'gray.900');
    const cardBg = useColorModeValue('white', 'gray.700');
    const headerBg = useColorModeValue('white', 'gray.700');

// Calcolo della percentuale di crediti utilizzati
const max = user.maxCredits || 0;
const remaining = user.remainingCredits || 0;

let creditUsage = 0;
if (max > 0) {
  creditUsage = ((max - remaining) / max) * 100;
  // Evita percentuali negative se hai più crediti del massimo
  if (creditUsage < 0) creditUsage = 0;
}

// Mappatura dei colori della progress bar di Chakra UI
const progressColor =
  creditUsage > 80 ? 'red' : creditUsage > 50 ? 'yellow' : 'green';



    // Funzione per gestire la chiamata al backend
    const convertXmlToPdf = async (files) => {
        // 1. Creazione del FormData
        // Dobbiamo usare FormData per inviare i file (richiesto da Multer sul backend)
        const formData = new FormData();
        files.forEach((file) => {
            // 'files' deve corrispondere al campo 'files' usato in upload.array('files')
            formData.append('files', file); 
        });

        // 2. Chiamata API protetta
        const token = localStorage.getItem('token'); // Recupera il token JWT
        
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/conversion/batch-convert`, {
                method: 'POST',
                // NON impostare Content-Type: 'multipart/form-data'. Il browser lo fa
                // automaticamente e correttamente quando si usa FormData.
                body: formData,
                headers: {
                    'Authorization': `Bearer ${token}`, // Autenticazione JWT
                },
            });

            const data = await response.json();

    if (response.ok) {
    return {
        success: true,
        convertedFiles: data.convertedFiles,
        newRemainingCredits: data.newRemainingCredits,
        zipFile: data.zipFile, // nome del file restituito dal backend
        downloadUrl: `${process.env.REACT_APP_API_BASE_URL}/download/${data.zipFile}` // URL completo per il download
    };
    } else {
                // Gestione errori come crediti insufficienti (status 402)
                console.error("Errore risposta API:", data.message);
                return { success: false, message: data.message || 'Errore sconosciuto API.' };
            }
        } catch (error) {
            console.error('Errore di rete o di sistema:', error);
            throw error;
        }
    };
    // Gestione del caricamento dei file (Drag & Drop o input)
const handleFileDrop = useCallback((e) => {
  e.preventDefault();
  e.stopPropagation();

  const files = e.dataTransfer
    ? Array.from(e.dataTransfer.files)
    : Array.from(e.target.files);

  // Filtra solo i tipi supportati
  const validFiles = files.filter(file => {
    const name = file.name.toLowerCase();
    return name.endsWith('.xml') || name.endsWith('.p7m') || name.endsWith('.zip');
  });

  if (validFiles.length > 0) {
    setUploadedFiles(validFiles);
    setConversionStatus(`Pronto per convertire ${validFiles.length} file (XML, P7M o ZIP).`);
  } else {
    setConversionStatus('Nessun file valido trovato. Carica XML, P7M o ZIP.');
  }
}, []);



    const handleConversion = async () => {
  if (uploadedFiles.length === 0) {
    setConversionStatus('Caricare prima i file (XML, P7M o ZIP).');
    return;
  }

  if (uploadedFiles.length > user.remainingCredits) {
    setConversionStatus(`Crediti insufficienti. Hai bisogno di ${uploadedFiles.length} crediti, ma ne hai solo ${user.remainingCredits}.`);
    return;
  }

  setIsLoading(true);
  setConversionStatus(`Avvio conversione di ${uploadedFiles.length} file...`);

  try {
    const formData = new FormData();
    uploadedFiles.forEach(file => {
      formData.append("files", file);
    });

    const token = localStorage.getItem("token");
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/conversion/batch-convert`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    });

    // 🛑 NUOVO BLOCCO DI GESTIONE TOKEN 🛑
    if (response.status === 401) {
      // 1. Rimuovi i dati locali (pulizia)
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      
      // 2. Imposta lo stato di errore (per mostrare lo Snackbar/Notifica)
      setConversionStatus("Sessione scaduta. Verrai reindirizzato al login."); 

      // 3. Reindirizza dopo un breve ritardo per mostrare la notifica
      setTimeout(() => {
        // Se usi React Router, sostituisci con l'azione di reindirizzamento del router (es. history.push('/login'))
        navigate('/login'); // Funziona in JS Vanilla/React se non usi history
      }, 1500); 

      // Termina l'esecuzione per evitare di processare la risposta come successo/errore standard
      setIsLoading(false);
      return; 
    }
    // ------------------------------------
    const result = await response.json();

    if (result.success) {
      const updatedUser = {
        ...user,
        remainingCredits: result.newRemainingCredits,
        lastZipFile: result.zipFile,
        conversionHistory: [
          ...(user.conversionHistory || []),
          {
            date: new Date().toISOString(),
            files: result.convertedFiles,
            zipFile: result.zipFile
          }
        ]
      };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));

      setConversionStatus(`Conversione di ${result.convertedFiles} file completata!`);
      setDownloadUrl(`${process.env.REACT_APP_API_BASE_URL}/download/${result.zipFile}`);
      setUploadedFiles([]);
    } else {
      setConversionStatus("Errore durante la conversione. Riprova più tardi.");
    }
  } catch (error) {
    setConversionStatus("Errore di sistema. Controllare la console.");
    console.error("Conversion Error:", error);
  } finally {
    setIsLoading(false);
  }
};

    const handleDownload = async (zipFile) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/download/${zipFile}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Errore download');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = zipFile;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (error) {
            console.error('Errore durante il download:', error);
        }
    };
    
    // Stili per l'area di Drag & Drop 
    const dragDropProps = {
        border: '4px',
        borderStyle: 'dashed',
        borderColor: 'indigo.300',
        _hover: { borderColor: 'indigo.500' },
        transition: 'all 0.3s',
        bg: 'indigo.50',
        opacity: 0.8, 
        p: 12,
        borderRadius: 'xl',
        textAlign: 'center',
        cursor: 'pointer',
    };
    
    // Logica per lo stile dello stato di conversione
    const getStatusStyle = () => {
        if (conversionStatus.includes('Crediti insufficienti')) {
            return { bg: 'red.100', color: 'red.700' };
        }
        if (conversionStatus.includes('completata')) {
            return { bg: 'green.100', color: 'green.700' };
        }
        if (conversionStatus) {
            return { bg: 'blue.100', color: 'blue.700' };
        }
        return { color: 'gray.500' };
    };

    const statusStyle = getStatusStyle();


if (!user) return <p>Caricamento...</p>;

    return (
       
        <Box minH="100vh" bg={bgApp} p={{ base: 4, sm: 8 }} fontFamily="sans-serif">
            <Box maxW="6xl" mx="auto">
                {/* Header Dinamico */}
                <Box as="header" mb={8} p={4} bg={headerBg} shadow="lg" borderRadius="xl">
                    <Flex direction={{ base: 'column', sm: 'row' }} justify="space-between" align="center">
                        <Heading 
                            as="h1" 
                            size="xl" 
                            fontWeight="bold" 
                            color="indigo.700" 
                            display="flex" 
                            alignItems="center"
                            mb={{ base: 3, sm: 0 }}
                        >
                            {/* Icona Zap da Lucide React */}
                            <Zap size={32} style={{ marginRight: '8px' }}/> XML2PDF Batch Converter
                        </Heading>
                        {/* FIX: Sostituito spaceX={4} con gap={4} per la spaziatura in Flex */}
                        <Flex align="center" mt={{ base: 3, sm: 0 }} gap={4}> 
                            <Box textAlign="right">
                                <Text fontSize="lg" fontWeight="semibold" color="gray.700">{user.userName}</Text>
                                <Text fontSize="sm" color="gray.500">
                                    {user.email} | ID: <ChakraLink as="span" fontSize="xs" fontWeight="mono" bg="gray.100" p={1} borderRadius="md">{user.id}</ChakraLink>
                                </Text>
                            </Box>
                            <Button 
                                onClick={handleLogout} 
                                p={3} 
                                bg="red.500" 
                                color="white" 
                                borderRadius="full" 
                                _hover={{ bg: 'red.600' }} 
                                transition="all 0.2s"
                                aria-label="Logout"
                            >
                                {/* Icona LogOut da Lucide React */}
                                <LogOut size={20}/>
                            </Button>
                        </Flex>
                    </Flex>
                </Box>

                {/* Sezione Crediti e Stato Abbonamento */}
                <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} mb={8}>
                
   {/* Card Crediti */}
      <Box bg="white" p={6} borderRadius="xl" shadow="lg" border="1px" borderColor="indigo.200">
        <Flex align="center" justify="space-between">
          <Heading as="h2" size="md" fontWeight="semibold" color="gray.700" display="flex" alignItems="center">
            <CloudUpload size={20} style={{ marginRight: '8px' }} color="indigo.500"/> Crediti Rimanenti
          </Heading>
          <Text fontSize="3xl" fontWeight="extrabold" color="indigo.600">{user.remainingCredits}</Text>
                  <Text fontSize="3xl" fontWeight="extrabold" color="indigo.600">{user.credits}</Text>
        </Flex>
        <Text fontSize="sm" color="gray.500" mt={1}>su {user.maxCredits} conversioni totali.</Text>


        <Box mt={4}>
          <Progress value={creditUsage} size="sm" colorScheme={progressColor} borderRadius="full"/>
          <Text fontSize="xs" textAlign="right" color="gray.500" mt={1}>Utilizzo: {creditUsage.toFixed(1)}%</Text>
        </Box>
      </Box>

      {/* Card Piano Attivo */}
      <Box bg="white" p={6} borderRadius="xl" shadow="lg" border="1px" borderColor="indigo.200">
        <Flex align="center">
          <User size={20} style={{ marginRight: '12px' }} color="green.500"/>
          <Heading as="h2" size="md" fontWeight="semibold" color="gray.700">Piano Attivo</Heading>
        </Flex>
        <Text fontSize="2xl" fontWeight="bold" color="green.600" mt={2}>{user.subscriptionPlan}</Text>
        <Text fontSize="sm" color="gray.500" mt={1}>Rinnovo: 20/11/2026 (Simulato)</Text>
      </Box>
                    
                    {/* Card Acquista Crediti */}
                    <Flex bg={cardBg} p={6} borderRadius="xl" shadow="lg" border="1px" borderColor="indigo.200" direction="column" justify="center" align="start">
                        <Flex align="center">
                            {/* Icona CreditCard da Lucide React */}
                            <CreditCard size={20} style={{ marginRight: '12px' }} color="red.500"/>
                            <Heading as="h2" size="md" fontWeight="semibold" color="gray.700">Gestione Abbonamento</Heading>
                        </Flex>
                        <ChakraLink 
                            as={Link}
                            to="/billing"
                            mt={3} 
                            w="full" 
                            textAlign="center" 
                            bg="red.600" 
                            color="white" 
                            py={2} 
                            px={4} 
                            borderRadius="lg" 
                            fontWeight="bold" 
                            _hover={{ bg: 'red.700' }} 
                            transition="all 0.2s"
                        >
                            Piani e Ricariche
                        </ChakraLink>
                    </Flex>
                </SimpleGrid>
                
                {/* Sezione Caricamento File */}
                <Box as="section" bg={cardBg} p={6} borderRadius="xl" shadow="xl">
                    <Heading as="h2" size="lg" fontWeight="bold" color="indigo.700" mb={4}>Conversione Batch (XML &rarr; PDF)</Heading>
                    
                    <Box 
                        {...dragDropProps}
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        onDrop={handleFileDrop}
                        onClick={() => document.getElementById('file-upload').click()}
                    >
                        {/* Icona Upload da Lucide React */}
                        <Upload size={48} style={{ margin: '0 auto' }} color="indigo.500" mb={2}/>
                        <Text fontSize="lg" fontWeight="medium" color="indigo.700">Trascina e rilascia i file XML qui</Text>
                        <Text fontSize="sm" color="gray.500" mt={1}>oppure clicca per selezionare. Massimo {user.maxCredits} file per volta.</Text>
                        <input 
                            type="file" 
                            id="file-upload" 
                            multiple 
                            accept=".xml,.zip,.p7m" 
                            onChange={handleFileDrop} 
                            style={{ display: 'none' }} 
                            disabled={isLoading}
                        />
                    </Box>
                    
                    {uploadedFiles.length > 0 && (
                        <Box mt={4} p={4} bg="gray.50" borderRadius="lg" border="1px" borderColor="gray.200">
                            <Text as="h3" fontWeight="semibold" color="gray.700" mb={2}>File Caricati ({uploadedFiles.length}):</Text>
                            <Box as="ul" maxH="40" overflowY="auto" fontSize="sm" spacing={1}>
                                {uploadedFiles.map((file, index) => (
                                    <Flex as="li" key={index} justify="space-between" align="center" color="gray.600" py={1}>
                                        <Text isTruncated>{file.name}</Text>
                                        <Text fontSize="xs" color="gray.400">{(file.size / 1024).toFixed(2)} KB</Text>
                                    </Flex>
                                ))}
                            </Box>
                        </Box>
                    )}

                    <Flex mt={6} direction={{ base: 'column', sm: 'row' }} justify="space-between" align="center">
                        <Box p={3} borderRadius="lg" fontSize="sm" fontWeight="medium" {...statusStyle} mb={{ base: 4, sm: 0 }}>
                            {isLoading ? (
                                <Flex align="center">
                                    <Spinner size="sm" mr={2}/>
                                    <Text>{conversionStatus}</Text>
                                </Flex>
                            ) : (
                                conversionStatus || 'In attesa di caricamento file...'
                            )}
                        </Box>

                        <Button
                            onClick={handleConversion}
                            disabled={isLoading || uploadedFiles.length === 0 || uploadedFiles.length > user.remainingCredits}
                            px={6} 
                            py={3} 
                            borderRadius="lg" 
                            fontWeight="bold" 
                            transition="all 0.3s"
                            bg={isLoading || uploadedFiles.length === 0 || uploadedFiles.length > user.remainingCredits ? 'gray.400' : 'indigo.600'}
                            color="white"
                            shadow={isLoading || uploadedFiles.length === 0 || uploadedFiles.length > user.remainingCredits ? 'md' : 'lg'}
                            _hover={isLoading || uploadedFiles.length === 0 || uploadedFiles.length > user.remainingCredits ? {} : { bg: 'indigo.700', shadow: 'xl' }}
                            cursor={isLoading || uploadedFiles.length === 0 || uploadedFiles.length > user.remainingCredits ? 'not-allowed' : 'pointer'}
                        >
                            {isLoading ? 'In Conversione...' : `Converti ${uploadedFiles.length} File`}
                        </Button>
                    </Flex>
                    
                    {uploadedFiles.length > 0 && uploadedFiles.length > user.remainingCredits && (
                        <Flex mt={4} p={3} bg="red.100" color="red.700" borderRadius="lg" fontSize="sm" fontWeight="medium" align="center">
                            {/* Icona ZapOff da Lucide React */}
                            <ZapOff size={20} style={{ marginRight: '8px' }}/>
                            Attenzione: Il numero di file ({uploadedFiles.length}) supera i tuoi crediti rimanenti ({user.remainingCredits}). Ricarica per procedere.
                        </Flex>
                    )}
                </Box>

                     {/* Bottone Download ZIP */}

                     {/* Feedback conversione */}
{conversionStatus && (
                  <Box bg="white" p={6} borderRadius="xl" shadow="lg" border="1px" borderColor="indigo.200" mt={4}>
                    <Flex align="center" justify="space-between">
                      <Heading as="h2" size="md" fontWeight="semibold" color="gray.700" display="flex" alignItems="center">
                        <CheckCircle size={20} style={{ marginRight: '8px' }} color="green.500"/>
                        Conversione completata
                      </Heading>
                      {downloadFile && (
                        <Button colorScheme="blue" size="sm" onClick={() => handleDownload(downloadFile)}>
                          Scarica ZIP
                        </Button>
                      )}
                    </Flex>
                    <Text fontSize="sm" color="gray.500" mt={2}>{conversionStatus}</Text>
                  </Box>
                )}



{/* Card persistente ultimo pacchetto */}
                {user.lastZipFile && (
                  <Box bg="white" p={6} borderRadius="xl" shadow="lg" border="1px" borderColor="indigo.200" mt={4}>
                    <Flex align="center" justify="space-between">
                      <Heading as="h2" size="md" fontWeight="semibold" color="gray.700" display="flex" alignItems="center">
                        <Download size={20} style={{ marginRight: "8px" }} color="blue.500"/>
                        Pacchetto Convertito
                      </Heading>
                      <Button colorScheme="blue" size="sm" onClick={() => handleDownload(user.lastZipFile)}>
                        Scarica ZIP
                      </Button>
                    </Flex>
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      Ultimo pacchetto generato pronto al download.
                    </Text>
                  </Box>
                )}

                
                <Box as="footer" mt={8} pt={4} borderTop="1px" borderColor="gray.200" textAlign="center">
                    <Text fontSize="sm" color="gray.500">
                        [Footer: Filestudio.it - Soluzioni per la Consulenza del Lavoro]
                    </Text>
                </Box>
            </Box>
        </Box>
    );
};

export default Dashboard;